import TextField from '@mui/material/TextField';
import type { FieldErrors, FieldValues, Path, UseFormRegister } from 'react-hook-form';

import { Field } from '@/components/ui/Field';

/** The four field names this group registers, which differ per form. */
export interface ContactFieldNames<T extends FieldValues> {
  name: Path<T>;
  title: Path<T>;
  email: Path<T>;
  phone: Path<T>;
}

interface ContactFieldsProps<T extends FieldValues> {
  /**
   * Blank leaves the labels as "Contact name"; "Billing" makes them read
   * "Billing contact name".
   *
   * Separate from `names` on purpose. These are words a person reads; the names
   * are what the form matches values and errors against, and the two do not
   * follow the same shape.
   */
  labelPrefix?: string;
  names: ContactFieldNames<T>;
  register: UseFormRegister<T>;
  /**
   * Needed as well as `register`, because each box hands its own message to
   * `Field` alongside its label. Without these the group has nothing to show
   * when a value is refused.
   */
  errors: FieldErrors<T>;
  /** True while the form may be read but not changed. */
  disabled?: boolean;
}

/** Reads one flat field's message, which is all these four names ever are. */
function messageFor<T extends FieldValues>(
  errors: FieldErrors<T>,
  name: Path<T>,
): string | undefined {
  const error = errors[name as keyof FieldErrors<T>];
  const message = (error as { message?: unknown } | undefined)?.message;
  return typeof message === 'string' ? message : undefined;
}

/**
 * The four parts of a contact: who they are, what they do, and two ways to
 * reach them.
 *
 * Written once because the account page shows two of them at the same time -
 * the organization's own contact and the billing contact - and two copies of
 * four fields and their labels drift apart within a week.
 */
export function ContactFields<T extends FieldValues>({
  labelPrefix = '',
  names,
  register,
  errors,
  disabled,
}: ContactFieldsProps<T>) {
  // "contact name" becomes "Billing contact name" with a prefix and
  // "Contact name" without one, which is what the details form already says.
  const label = (rest: string) =>
    labelPrefix ? `${labelPrefix} ${rest}` : rest.charAt(0).toUpperCase() + rest.slice(1);

  return (
    <>
      <Field label={label('contact name')} error={messageFor(errors, names.name)}>
        <TextField {...register(names.name)} disabled={disabled} />
      </Field>

      <Field label={label('contact job title')} error={messageFor(errors, names.title)}>
        <TextField {...register(names.title)} disabled={disabled} />
      </Field>

      <Field
        label={label('contact email address')}
        error={messageFor(errors, names.email)}
      >
        <TextField type="email" {...register(names.email)} disabled={disabled} />
      </Field>

      <Field label={label('contact phone')} error={messageFor(errors, names.phone)}>
        <TextField {...register(names.phone)} disabled={disabled} />
      </Field>
    </>
  );
}
